import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  Ticket,
  TrendingUp,
  Users,
  Coins,
  Copy,
  CheckCircle2,
  Share2,
  Wallet,
  CircleDot,
  ArrowUpRight,
  ShoppingCart,
  ChevronDown,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import CycleProgress from "@/components/CycleProgress";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [sponsor, setSponsor] = useState(null);
  const [openProfile, setOpenProfile] = useState(false);
  const [openReferral, setOpenReferral] = useState(false);
  const [openFriends, setOpenFriends] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, r, l, me] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/dashboard/referrals"),
          api.get("/dashboard/referral-link"),
          api.get("/auth/me"),
        ]);
        setSummary(s.data);
        setReferrals(r.data.referrals);
        const origin = window.location.origin;
        setLink(`${origin}/registracija?ref=${l.data.code}`);
        setSponsor(me.data.sponsor || null);
      } catch (e) {
        toast.error("Greška pri učitavanju podataka");
      }
    })();
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Pozivni link kopiran!");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Nije moguće kopirati");
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "NAŠA Myner Adria",
          text: "Pridruži se NAŠA Myner Adria zajednici i osvoji NAŠA Tokene!",
          url: link,
        });
      } catch {}
    } else {
      copyLink();
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="dashboard-root">
      <div className="flex justify-end mb-2">
        <a href="https://www.myner-adria.com" className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#22ff77] transition-colors">
          <ArrowLeft className="w-3 h-3" /> Početna
        </a>
      </div>
      {/* Welcome */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-zinc-500">Kontrolna tabla</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-wide mt-1">
            Dobrodošli, <span className="text-neon-green">{user?.name?.split(" ")[0] || "Člane"}</span>
          </h1>
          {sponsor && (
            <div
              data-testid="sponsor-info"
              className="mt-3 inline-flex items-center gap-2 border border-[#ff7a18]/40 bg-[#ff7a18]/5 px-3 py-1.5 rounded-sm text-xs"
            >
              <span className="text-zinc-500 uppercase tracking-[0.2em] text-[10px]">Vaš sponzor:</span>
              <span className="text-[#ff7a18] font-display font-bold tracking-wide" data-testid="sponsor-name">
                {sponsor.name}
              </span>
              {sponsor.is_root && (
                <span className="text-[9px] uppercase tracking-widest text-zinc-500 border border-white/10 px-1.5 py-0.5 rounded-sm ml-1">
                  Root link
                </span>
              )}
            </div>
          )}
        </div>
        <div className="text-xs text-zinc-500">
          Status učešća:{" "}
          <span className={user?.investment_status === "aktivan" ? "text-[#22ff77]" : "text-[#ff7a18]"}>
            {user?.investment_status?.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Hero ticket counter */}
      <div className="relative nasa-card overflow-hidden">
        <div className="absolute inset-0 nasa-card-bg-man-hero" />

        <div className="absolute inset-0 nasa-grid-bg opacity-60" />
        <div className="relative p-8 md:p-12 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 text-[#22ff77] uppercase tracking-[0.4em] text-[10px]">
              <Ticket className="w-4 h-4" strokeWidth={1.5} />
              NAŠA Tokeni · Giveaway
            </div>
            <div
              data-testid="tickets-counter"
              className="font-display text-7xl md:text-9xl font-bold text-neon-green mt-3 animate-pulse-neon"
            >
              {summary?.tickets ?? "—"}
            </div>
            <p className="text-zinc-400 text-sm mt-2 max-w-xl leading-relaxed">
              Vaš trenutni bilans <span className="text-white">NAŠA Tokena</span> za aktuelni Giveaway ciklus.
              Pozovite prijatelje i iskoristite tokene za otključavanje premijum nagrada:{" "}
              <span className="text-[#22ff77]">Dve rate za najam mašine (paket 1.200€)</span> i{" "}
              <span className="text-[#ff7a18]">ekskluzivan pristup Arbitraži PRO</span>.
            </p>
            <Link
              to="/kupi-tikete"
              data-testid="cta-buy-tickets"
              className="mt-5 inline-flex items-center gap-2 bg-[#ff7a18] text-black font-bold px-5 py-3 uppercase tracking-[0.2em] text-xs rounded-sm hover:brightness-110 glow-orange"
            >
              <ShoppingCart className="w-4 h-4" /> Kupi tiket · 25€ = 25 tokena
            </Link>
          </div>
          <div className="space-y-3">
            <MiniStat
              icon={Users}
              label="Pozvani prijatelji"
              value={summary?.total_referrals ?? 0}
              accent="orange"
            />
            <MiniStat
              icon={TrendingUp}
              label="Aktivni članovi"
              value={summary?.active_referrals ?? 0}
              accent="green"
            />
            <MiniStat
              icon={Coins}
              label="Tokeni od referala"
              value={summary?.tickets_from_referrals ?? 0}
              accent="green"
            />
          </div>
        </div>
      </div>

      {/* Continuous Giveaway Cycle */}
      <CycleProgress />

      {/* Profile + Referral link */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile card */}
        <div className="nasa-card lg:col-span-1" data-testid="profile-card">
          <button onClick={() => setOpenProfile(o => !o)} className="md:hidden w-full flex items-center justify-between p-6">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-zinc-500">
              <CircleDot className="w-3 h-3 text-[#ff7a18]" /> Moj profil
            </div>
            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${openProfile ? "rotate-180" : ""}`} />
          </button>
          <div className={`${openProfile ? "block" : "hidden"} md:block p-6 pt-0 md:pt-6`}>
          <div className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-4">
            <CircleDot className="w-3 h-3 text-[#ff7a18]" /> Moj profil
          </div>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-sm bg-[#22ff77]/10 border border-[#22ff77]/40 overflow-hidden flex items-center justify-center text-2xl font-display font-bold text-[#22ff77]">
              {user?.picture ? (
                <img src={user.picture} alt={user?.name || "avatar"} data-testid="dashboard-avatar" className="w-full h-full object-cover" />
              ) : (
                (user?.name || "?").charAt(0).toUpperCase()
              )}
            </div>
            <div>
              <div className="font-display text-lg font-semibold leading-tight" data-testid="profile-name">{user?.name}</div>
              <div className="text-xs text-zinc-500" data-testid="profile-email">{user?.email}</div>
            </div>
          </div>
          <ProfileRow label="Telefon" value={user?.phone || "—"} testId="profile-phone" />
          <ProfileRow label="Država" value={user?.country || "—"} testId="profile-country" />
          <ProfileRow
            label="Status"
            value={user?.investment_status?.toUpperCase()}
            accent={user?.investment_status === "aktivan" ? "green" : "orange"}
            testId="profile-status"
          />
          <ProfileRow
            label="Saldo"
            value={`€ ${(user?.balance ?? 0).toFixed(2)}`}
            accent="green"
            testId="profile-balance"
          />
          <ProfileRow
            label="Pozivni kod"
            value={user?.referral_code}
            accent="orange"
            testId="profile-refcode"
          />
          </div>
        </div>

        {/* Referral link card */}
        <div className="nasa-card lg:col-span-2" data-testid="referral-card">
          <button onClick={() => setOpenReferral(o => !o)} className="md:hidden w-full flex items-center justify-between p-6">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-zinc-500">
              <Share2 className="w-3 h-3 text-[#22ff77]" /> Pozivni link
            </div>
            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${openReferral ? "rotate-180" : ""}`} />
          </button>
          <div className={`${openReferral ? "block" : "hidden"} md:block p-6 pt-0 md:pt-6`}>
          <div className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-4">
            <Share2 className="w-3 h-3 text-[#22ff77]" /> Pozivni link
          </div>
          <h2 className="font-display text-2xl font-semibold tracking-wide">
            Pozovi prijatelje, <span className="text-neon-orange">osvoji tokene</span>
          </h2>
          <p className="text-sm text-zinc-400 mt-1 mb-5">
            Svaki prijatelj koji se registruje vašim linkom donosi vam <span className="text-[#22ff77]">+1 NAŠA Token</span>.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="referral-link-input"
              readOnly
              value={link}
              className="flex-1 bg-black border border-white/10 px-3 py-3 text-xs sm:text-sm font-mono text-[#22ff77] rounded-sm focus:outline-none focus:border-[#22ff77]"
            />
            <button
              data-testid="copy-referral-button"
              onClick={copyLink}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-[#22ff77] text-black font-bold uppercase tracking-widest text-xs rounded-sm hover:brightness-110 transition glow-green"
            >
              {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Kopirano" : "Kopiraj"}
            </button>
            <button
              data-testid="share-referral-button"
              onClick={shareLink}
              className="flex items-center justify-center gap-2 px-4 py-3 border border-[#ff7a18]/60 text-[#ff7a18] font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-[#ff7a18]/10 transition"
            >
              <Share2 className="w-4 h-4" />
              Podeli
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-6">
            <BigStat
              label="Tokeni od referala"
              value={`€${(summary?.balance ?? 0).toFixed(0)}`}
              icon={Wallet}
              accent="green"
            />
            <BigStat
              label="Ulog prijatelja"
              value={`€${(summary?.total_invested_by_referrals ?? 0).toFixed(0)}`}
              icon={TrendingUp}
              accent="orange"
            />
            <BigStat
              label="Aktivni"
              value={summary?.active_referrals ?? 0}
              icon={Users}
              accent="green"
            />
          </div>

          <a
            href="/pozovi"
            className="referral-rewards-btn mt-5 w-full flex items-center justify-center gap-2 py-3 rounded-sm text-black font-bold uppercase tracking-[0.2em] text-xs relative overflow-hidden"
          >
            <span className="referral-rewards-ring referral-rewards-ring-1" />
            <span className="referral-rewards-ring referral-rewards-ring-2" />
            <span className="relative z-10">🎁 Nagrade za referale</span>
          </a>
          </div>
        </div>
      </div>

      {/* Friends table */}
      <div className="nasa-card overflow-hidden" data-testid="friends-table-card">
        <button onClick={() => setOpenFriends(o => !o)} className="md:hidden w-full flex items-center justify-between p-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">Lista prijatelja</div>
            <h2 className="font-display text-xl font-semibold mt-1">Pozvani prijatelji</h2>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            Ukupno: <span className="text-[#22ff77] font-bold" data-testid="friends-count">{referrals.length}</span>
            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${openFriends ? "rotate-180" : ""}`} />
          </div>
        </button>
        <div className="hidden md:flex items-center justify-between p-6 border-b border-white/5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">Lista prijatelja</div>
            <h2 className="font-display text-xl font-semibold mt-1">Pozvani prijatelji</h2>
          </div>
          <div className="text-xs text-zinc-500">
            Ukupno: <span className="text-[#22ff77] font-bold">{referrals.length}</span>
          </div>
        </div>
        <div className={`${openFriends ? "block" : "hidden"} md:block`}>

        {referrals.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm" data-testid="friends-empty">
            Još uvek niste pozvali nikoga. Podelite svoj pozivni link i počnite da skupljate NAŠA Tokene.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="friends-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 border-b border-white/5">
                  <th className="text-left px-6 py-3 font-medium">#</th>
                  <th className="text-left px-6 py-3 font-medium">Ime</th>
                  <th className="text-left px-6 py-3 font-medium">Email</th>
                  <th className="text-left px-6 py-3 font-medium">Datum</th>
                  <th className="text-left px-6 py-3 font-medium">Status</th>
                  <th className="text-right px-6 py-3 font-medium">Ulog</th>
                  <th className="text-right px-6 py-3 font-medium">Tokeni</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((r, i) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02] transition" data-testid={`friend-row-${i}`}>
                    <td className="px-6 py-4 text-zinc-500">{String(i + 1).padStart(2, "0")}</td>
                    <td className="px-6 py-4 font-medium">{r.name}</td>
                    <td className="px-6 py-4 text-zinc-400 text-xs">{r.email}</td>
                    <td className="px-6 py-4 text-zinc-400 text-xs font-mono">
                      {new Date(r.joined_at).toLocaleDateString("sr-Latn-RS")}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border ${
                          r.status === "aktivan"
                            ? "text-[#22ff77] border-[#22ff77]/40 bg-[#22ff77]/5"
                            : "text-[#ff7a18] border-[#ff7a18]/40 bg-[#ff7a18]/5"
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-mono">{r.investment_amount.toFixed(2)} €</td>
                    <td className="px-6 py-4 text-right">
                      <span className="inline-flex items-center gap-1 text-[#22ff77] font-bold">
                        <Ticket className="w-3.5 h-3.5" /> {r.tickets_earned}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>

      <p className="text-center text-[10px] text-zinc-600 tracking-widest uppercase py-6 flex items-center justify-center gap-2">
        Powered by NAŠA Myner Adria <ArrowUpRight className="w-3 h-3" />
      </p>
    </div>
  );
}

function ProfileRow({ label, value, accent, testId }) {
  const accentClass =
    accent === "green" ? "text-[#22ff77]" : accent === "orange" ? "text-[#ff7a18]" : "text-white";
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <span className={`text-sm font-mono ${accentClass}`} data-testid={testId}>{value}</span>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, accent }) {
  const color = accent === "orange" ? "#ff7a18" : "#22ff77";
  return (
    <div className="flex items-center justify-between border border-white/10 bg-black/40 px-5 py-4 rounded-sm">
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-widest text-zinc-400 underline">{label}</span>
      </div>
      <span className="font-display font-bold text-3xl" style={{ color }}>
        {value}
      </span>
    </div>
  );
}

function BigStat({ label, value, icon: Icon, accent }) {
  const color = accent === "orange" ? "#ff7a18" : "#22ff77";
  return (
    <div className="border border-white/10 bg-black/40 p-4 rounded-sm">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
        <Icon className="w-3 h-3" style={{ color }} />
        {label}
      </div>
      <div className="font-display text-2xl font-bold mt-1" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
